import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import NavBarLogo from '@assets/chrome/navbar-logo.svg';
import ContactCenterTitle from '@assets/contact-center-title.svg';
import paths from '@app/routes/paths';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import {
  BodyText,
  Icon,
  Stack,
  Announcement,
} from '@servicetitan/design-system';

import styles from './NavBar.module.css';
import UserMenu from './components/UserMenu/UserMenu';
import { permissionSets, RequirePermissions } from '@app/permissions';
import { useState } from 'react';
import OutboundCallModal from './components/OutboundCallModal/OutboundCallModal';
import noop from '@lib/utils/noop';
import { UserStatusMenu } from '@modules/UserStatusModule/components/UserStatusMenu';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import { mainLayoutLocators } from '@locators/main-layout';

type NavBarProps = {
  onToggleDebug?: () => void;
};

const NavBar = ({ onToggleDebug = noop }: NavBarProps) => {
  const { serviceName } = useAuth();
  const [showOutboundCallModal, setShowOutboundCallModal] = useState(false);
  const { pathname } = useLocation();
  const { phoneConnected, registrationOverwritten } = usePhoneState();

  const isSettings = pathname.startsWith(`/settings`);

  const handleNavLogoClick = () => {
    // if (environment.MODE === 'development') {
    onToggleDebug();
    // }
  };

  return (
    <>
      {!phoneConnected && registrationOverwritten && (
        <Announcement
          status='warning'
          title='You have multiple browser tabs open for Contact Center Pro. Only the most recently opened browser tab has phone functionality. Please go to that tab to place or receive phone calls.'
        />
      )}
      <Stack
        className={classNames('bg-neutral-300', styles.navBarContainer)}
        justifyContent='space-between'
      >
        <Stack>
          <Stack
            id={mainLayoutLocators.navBar.logo}
            onClick={handleNavLogoClick}
            className={classNames('bg-white', styles.logoContainer)}
            justifyContent='center'
            alignItems='center'
          >
            <NavBarLogo />
          </Stack>
          <Link to={paths.home} className='d-f m-l-1 align-items-center'>
            <ContactCenterTitle />
          </Link>
        </Stack>
        <Stack justifyContent='center' alignItems='center' spacing={3}>
          <BodyText className={classNames('c-neutral-70', 'ta-center')}>
            {serviceName}
          </BodyText>

          <button
            aria-label='Make an outbound call'
            id={mainLayoutLocators.navBar.callBtnIcon}
            className={classNames(
              styles.icon,
              styles.clickableButton,
              showOutboundCallModal && styles.activeIcon,
            )}
            onClick={() => setShowOutboundCallModal(!showOutboundCallModal)}
          >
            <Icon name='phone_outbound' size={24} />
          </button>
          <RequirePermissions
            permissions={permissionSets.admin.ALL}
            permissionsMatch='any'
          >
            <Link
              id={mainLayoutLocators.navBar.settingsBtn}
              className={classNames(
                styles.icon,
                isSettings && styles.activeIcon,
              )}
              to={paths.settings}
            >
              <Icon name='settings' size={24} />
            </Link>
          </RequirePermissions>

          <UserStatusMenu />
          <UserMenu />
        </Stack>
        <OutboundCallModal
          open={showOutboundCallModal}
          setOpen={setShowOutboundCallModal}
        />
      </Stack>
    </>
  );
};

export default NavBar;
